/**
 * @internal Do not import from outside SellPriceUI folder.
 */
import { FC, useState } from 'react';
import { Input, InputNumber, Typography, Button, theme } from 'antd';
import FlexCol from '../../../atoms/FlexCol';
import { CaretDownOutlined, CaretUpOutlined, MinusOutlined } from '@ant-design/icons';
import type { CaretDirection, ResolvedSellPriceLabels } from './types';
import {
  commitDecimalString,
  liveParsedDecimal,
  stripGroupingFromAmountDisplay,
} from './decimalDraftInput';

const { Text } = Typography;

type DetailedCompositePriceFormProps = {
  basePrice: number;
  calcOutput: number;
  ratePercent: number;
  caretDirection: CaretDirection;
  disabled?: boolean;
  status?: '' | 'error' | 'warning' | undefined;
  labels: ResolvedSellPriceLabels;
  onOutputChange: (newOutput: number | null) => void;
  onRatePercentChange: (newRatePercent: number | null) => void;
  onClear: () => void;
  showOperatorFields: boolean;
};

const DetailedCompositePriceForm: FC<DetailedCompositePriceFormProps> = ({
  basePrice,
  calcOutput,
  ratePercent,
  caretDirection,
  disabled = false,
  status,
  labels,
  onOutputChange,
  onRatePercentChange,
  onClear,
  showOperatorFields,
}) => {
  const { token } = theme.useToken();
  const [draftOutput, setDraftOutput] = useState<string | null>(null);
  const [draftRate, setDraftRate] = useState<string | null>(null);

  const formatCurrency = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined) return '';
    const num = Number(amount);
    if (!Number.isFinite(num)) return '';
    const hasDecimals = num % 1 !== 0;
    const formattedValue = hasDecimals ? num.toFixed(2) : num.toString();
    return formattedValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const caretIcon = caretDirection === 'neutral'
    ? <MinusOutlined style={{ color: token.colorTextQuaternary }} />
    : caretDirection === 'up'
      ? <CaretUpOutlined style={{ color: token.colorSuccess }} />
      : <CaretDownOutlined style={{ color: token.colorError }} />;

  const outputBlurredDisplay = formatCurrency(calcOutput);
  const rateBlurredDisplay = ratePercent.toFixed(2);

  return (
    <FlexCol style={{ width: 250, maxWidth: `calc(100vw - ${token.marginLG * 2}px)` }}>
      <FlexCol noGap>
        <Text>{labels.basePriceLabel}</Text>
        <InputNumber
          value={basePrice}
          disabled
          aria-label="composite-price-input-input"
          style={{ width: '100%' }}
          prefix="$"
          formatter={(value) => {
            if (value === undefined || value === null) return '';
            const parsed = Number(value);
            if (!Number.isFinite(parsed)) return String(value);
            return formatCurrency(parsed);
          }}
          parser={(value) => {
            const cleaned = (value ?? '').replace(/[^0-9.-]/g, '');
            const parsed = Number.parseFloat(cleaned);
            return Number.isFinite(parsed) ? parsed : NaN;
          }}
        />
      </FlexCol>
      {showOperatorFields && (
        <>
          <FlexCol gap={0} style={{ flex: 1 }}>
            <Text>
              {labels.ratePercentLabel} {caretIcon}
            </Text>
            <Input
              value={draftRate === null ? rateBlurredDisplay : draftRate}
              onFocus={() => {
                setDraftRate(rateBlurredDisplay);
              }}
              onChange={(e) => {
                const next = e.target.value;
                setDraftRate(next);
                const live = liveParsedDecimal(next);
                if (live !== undefined) onRatePercentChange(live);
              }}
              onBlur={() => {
                onRatePercentChange(commitDecimalString(draftRate ?? ''));
                setDraftRate(null);
              }}
              disabled={disabled}
              status={status}
              aria-label="composite-price-ratepercent-input"
              style={{ width: '100%', fontVariantNumeric: 'tabular-nums' }}
              suffix="%"
            />
          </FlexCol>

          <FlexCol noGap>
            <Text>{labels.sellPriceLabel}</Text>
            <Input
              prefix="$"
              value={draftOutput === null ? outputBlurredDisplay : draftOutput}
              onFocus={() => {
                setDraftOutput(stripGroupingFromAmountDisplay(outputBlurredDisplay));
              }}
              onChange={(e) => {
                const next = e.target.value;
                setDraftOutput(next);
                const live = liveParsedDecimal(next);
                if (live !== undefined) onOutputChange(live);
              }}
              onBlur={() => {
                onOutputChange(commitDecimalString(draftOutput ?? ''));
                setDraftOutput(null);
              }}
              disabled={disabled}
              status={status}
              aria-label="composite-price-output-input"
              style={{ width: '100%', fontVariantNumeric: 'tabular-nums' }}
            />
          </FlexCol>
        </>
      )}

      <Text type="secondary">
        <small>{labels.formulaDisplay}</small>
      </Text>
      <FlexCol alignEnd>
        <Button
          type="link"
          onClick={onClear}
          disabled={disabled}
          aria-label="composite-price-clear-button"
        >
          Clear
        </Button>
      </FlexCol>
    </FlexCol>
  );
};

export default DetailedCompositePriceForm;
